import dayjs, { Dayjs } from 'dayjs';
import omit from 'lodash/omit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import {
  NormaliseNavData,
  normaliseNavDataAsync,
} from '@/modules/issuer-dashboard/helpers/normalizeNavData.ts';
import { useNavHistoryListQuery } from '@/modules/issuer-dashboard/queries/nav.tsx';
import { BalanceChartCanvas, ChartEmptyState, FilterItem, InfoCard, Spinner } from 'ui';
import { GetRegisterClassesWithNAVResponse } from 'api';

const chartTickAmount = 6;

interface NavOverTimeChartProps {
  register_class_ids?: number[];
}

export const NavOverTimeChart = ({ register_class_ids }: NavOverTimeChartProps) => {
  const [filters, setFilters] = useState<{ from_date?: string; to_date?: string }>({
    from_date: '2001-01-01',
  });

  const dateValue = useMemo(
    () =>
      filters.from_date && filters.to_date
        ? ([dayjs(filters.from_date), dayjs(filters.to_date)] as [Dayjs, Dayjs])
        : undefined,
    [filters.from_date, filters.to_date],
  );

  const handleChangeDate = useCallback(
    (date: [Dayjs, Dayjs] | null) => {
      setFilters(filters => {
        return date
          ? {
              ...filters,
              from_date: date[0].format('YYYY-MM-DD'),
              to_date: date[1].endOf('d').format('YYYY-MM-DD'),
            }
          : omit({ ...filters, from_date: '2001-01-01' }, ['to_date']);
      });
    },
    [setFilters],
  );
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();

  const { data, isLoading, isError, isFetching } = useNavHistoryListQuery(
    filters,
    false,
    registerId,
  );
  const [chartSeries, setChartSeries] = useState<NormaliseNavData[]>([]);
  const [isNormalizing, setIsNormalizing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setIsNormalizing(true);

    void normaliseNavDataAsync(
      { data: data?.data } as GetRegisterClassesWithNAVResponse,
      !dateValue?.[1],
    ).then(result => {
      if (cancelled) return;

      setChartSeries(
        result.filter(item => register_class_ids?.includes(item.id) || !register_class_ids),
      );
    }).catch(() => {
      if (!cancelled) {
        setChartSeries([]);
      }
    }).finally(() => {
      if (!cancelled) {
        setIsNormalizing(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [data?.data, dateValue, register_class_ids]);

  const isPresentChartRecords = useMemo(
    () => chartSeries.some(({ data }) => data.length),
    [chartSeries],
  );

  return (
    <InfoCard
      title={`NAV over time`}
      headerAction={
        <div className="flex items-center justify-end gap-2">
          {isFetching && !isLoading && <Spinner size="md" />}
          <FilterItem
            placeholder="Date"
            type="date"
            onChange={handleChangeDate}
            value={dateValue}
            className="rounded-lg"
          />
        </div>
      }
      classes={{
        content: 'p-0 px-6',
      }}
    >
      {!isLoading && !isNormalizing && isPresentChartRecords && (
        <BalanceChartCanvas
          data={chartSeries}
          height="350px"
          type="line"
          fill={false}
          withDateTime={true}
          colors={['#0369A1', '#0EA5E9', '#34D399', '#FACC15', '#A855F7']}
          countsLabel={chartTickAmount}
          markerStrokeColor="#FFFFFF"
        />
      )}
      {(isLoading || isNormalizing) && !chartSeries.length && (
        <div className="flex flex-col w-full h-[200px] items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {!isLoading && !isNormalizing && !isPresentChartRecords && (
        <div className="py-4 flex justify-center items-center font-medium text-lg">
          <ChartEmptyState isError={isError} isLoading={isLoading} />
        </div>
      )}
    </InfoCard>
  );
};
